import { useState, type FormEvent } from 'react';
import { Link, redirect } from 'react-router';
import { Logo } from '@/components/brand/Logo.tsx';
import { api } from '@/lib/api.ts';
import { fetchMe, landingPathFor } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput } from '@/components/ui/Field.tsx';

// If the user is already signed in, send them straight to their landing page.
export async function signInLoader() {
  const session = await fetchMe();
  // React Router data-router idiom: throw redirect() returns a Response.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (session) throw redirect(landingPathFor(session.role));
  return null;
}

export function SignIn() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.post('/auth/request', { email });
      setSubmitted(true);
    } catch {
      // Server returns 202 even when the email is unknown (DESIGN §4.6 enumeration
      // defence). Any thrown error here is a real network/server failure.
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <Centered>
        <Logo className="text-2xl" />
        <h1 className="mt-6 text-2xl font-semibold">Check your inbox</h1>
        <p className="mt-2 text-muted-foreground">
          If <span className="text-foreground">{email}</span> is registered, a sign-in link is on
          its way. Login links expire after 15 minutes.
        </p>
      </Centered>
    );
  }

  return (
    <Centered>
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">Sign in to besideU</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We'll email you a one-time link. No passwords.
      </p>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        <Field label="Work email" error={error ?? undefined}>
          {(id, descProps) => (
            <TextInput
              id={id}
              {...descProps}
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Button type="submit" loading={pending}>
          Send link
        </Button>
      </form>
      <p className="mt-8 text-sm text-muted-foreground">
        Need an account? Managers and Employees are invited by HR. To create a new organisation,{' '}
        <Link
          to="/sign-up"
          className="text-foreground underline underline-offset-2 hover:no-underline"
        >
          sign up here
        </Link>
        .
      </p>
      <p className="mt-3 text-xs text-muted-foreground">
        Operator or adviser? See{' '}
        <Link
          to="/login"
          className="text-foreground underline underline-offset-2 hover:no-underline"
        >
          all sign-in options
        </Link>
        .
      </p>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      {children}
    </main>
  );
}
